import clickToDownloadView from './clickToDownloadComponent.html';
import './clickToDownloadComponent.css';
import { AuthTypes } from 'components/sharpr/auth/AuthTypes';

export function clickToDownloadComponent() {
	return {
		bindings: {
			file: '<',
		},
		transclude: true,
		controllerAs: 'vm',
		controller: clickToDownloadComponentCtrl,
		template: clickToDownloadView,
	};
}

export class clickToDownloadComponentCtrl {
	constructor($element, EventTracker, storage) {
		'ngInject';
		this.element = $element[0];
		this.EventTracker = EventTracker;
		this.storage = storage;
		this.progress = this.element.querySelector('progress');
		this.hideProgress();
		this._anchor = document.createElement('a');
		this._anchor.addEventListener('click', evt => evt.stopPropagation());
		this.supportsDownloadAttr = 'download' in this._anchor;
		this.supportsFileTransfer = typeof FileTransfer != 'undefined';
	}

	download() {
		if (this.supportsFileTransfer) {
			// cordova file transfer
			this.doFileTransfer();
		} else if (this.supportsDownloadAttr) {
			// html5 download attribute on <a>
			this.doHtml5Download();
		} else {
			// desktop: just use window.location.href
			this.doUrlDownload();
		}
	}

	initProgress() {
		this.progress.style.display = '';
	}

	showProgress(done, total) {
		this.progress.setAttribute('value', done);
		this.progress.setAttribute('max', total);
	}

	hideProgress() {
		this.progress.style.display = 'none';
	}

	track(trigger_type, error = '') {
		this.EventTracker.save('download', {
			attachment_id: this.file.id,
			mime_type: this.getMime(),
			extension: this.getExtension(),
			trigger_type,
			error,
		});
	}

	getFilename() {
		return this.file.name || this.file.filename;
	}

	getExtension() {
		const name = String(this.getFilename());
		const match = name.match(/\.(\w+)$/);
		return match ? match[1] : null;
	}

	getUrl() {
		const urlParam = AuthTypes.getUrlParam('', this.storage);
		return `${window.BASE_URL}/api/v2/files/${this.file.id}/download?disposition=attachment&${urlParam}`;
	}

	getMime() {
		return this.file.mime_type;
	}

	open(entry) {
		const filePath = (window.decodeURI || window.unescape)(entry.toURL());
		if (window.device && window.device.platform == 'iOS') {
			this.track('cordova_window_open');
			const inAppBrowser = window.open(filePath, '_blank', 'location=no,closebuttoncaption=Close,enableViewportScale=yes');
			// example to fire event on window close:
			// inAppBrowser.addEventListener('exit', onExit);
			// notify user if file viewer may turn up blank
			const openable = /\.(mp3|mp4|m4v|vp9|jpe?g|gif|png|pdf|pptx?|ppsx?|csv|xlsx?|docx?|rtf|txt)$/i;
			if (!filePath.match(openable)) {
				setTimeout(() => {
					(navigator.notification || window).alert('Unknown file format; file may not open correctly.');
				}, 1000);
			}
		} else if (cordova.plugins.fileOpener2.open) {
			const onOpen = () => {
				this.track('cordova_native_open');
			};
			const onError = error => {
				if (error.status === 1) {
					this.track('cordova_native_open', 'No native file viewer found');
					(navigator.notification || window).alert('Unknown file type; unable to open file.');
				} else {
					this.track('cordova_native_open', 'Error: ' + error.message);
					(navigator.notification || window).alert('Error opening file.');
				}
			};
			cordova.plugins.fileOpener2.open(filePath, this.getMime(), {
				success: onOpen,
				error: onError,
			});
		} else {
			this.track('cordova_native_open', 'file plugin not present');
			(navigator.notification || window).alert('Error opening file.');
		}
	}

	doFileTransfer() {
		this.initProgress();
		this._getLocalFileSystemPath(this.getFilename()).then(
			// resolved ok
			localPath => {
				const uri = encodeURI(this.getUrl());
				const onComplete = entry => {
					this.hideProgress();
					this.open(entry);
				};
				const onError = errMsg => {
					this.hideProgress();
					this.track('cordova', errMsg);
					(navigator.notification || window).alert('Error: Download Failed');
				};
				const trustAllHosts = false;
				const fileTransfer = new FileTransfer();
				fileTransfer.download(uri, localPath, onComplete, onError, trustAllHosts);
				fileTransfer.onprogress = evt => this.showProgress(evt.loaded, evt.total);
			},
			// rejected; unable to get download file path
			errMsg => {
				(navigator.notification || window).alert('Error: Download Failed');
			}
		);
	}

	doHtml5Download() {
		this._anchor.setAttribute('href', this.getUrl());
		this._anchor.setAttribute('download', this.getFilename());
		this.element.appendChild(this._anchor);
		this._anchor.click();
		this.element.removeChild(this._anchor);
		this.track('html5');
	}

	doUrlDownload() {
		window.location.href = this.getUrl();
		this.track('url');
	}

	_getLocalFileSystemPath(filename) {
		return new Promise((resolve, reject) => {
			// setup callbacks
			const fsSuccess = fs => {
				(fs.root || fs).getFile(
					filename,
					{
						create: true,
						exclusive: false,
					},
					handleFileEntry,
					fileEntryError
				);
			};
			const fsError = errMsg => {
				this.track('cordova', `Unable to get file system handle. Got error "${errorMsg}"`);
				reject(errMsg);
			};
			const fileEntryError = errMsg => {
				this.track('cordova', `Unable to get file handle. Got error "${errorMsg}"`);
				reject(errMsg);
			};
			const handleFileEntry = fileEntry => {
				resolve(fileEntry.toURL());
			};
			// request filesystem
			if (window.device && window.device.platform == 'Android' && window.resolveLocalFileSystemURL && window.cordova) {
				// android
				window.resolveLocalFileSystemURL(window.cordova.file.externalRootDirectory, fsSuccess, fsError);
			} else if (
				window.device &&
				window.device.platform == 'iOS' &&
				window.requestFileSystem &&
				typeof LocalFileSystem != 'undefined'
			) {
				// iOS
				window.requestFileSystem(LocalFileSystem.PERSISTENT, 0, fsSuccess, fsError);
			} else {
				this.track('cordova', 'Cordova plugin not found');
				reject('window.requestFileSystem not found AND window.resolveLocalFileSystemURL not found');
			}
		});
	}
}
